import axios from 'axios';
import React, { useState } from 'react';
import prestacaoContaService, { type PrestacaoAnexo } from '../../services/prestacaoContaService';
import { toUpperText } from '../../utils/formatters';

type Props = {
  anexo: PrestacaoAnexo;
  onChange: (atualizado: PrestacaoAnexo) => void;
};

/**
 * Publicar no portal da transparência o anexo de um comprovante (TAEP D.3).
 * Só aparece para quem tem PUBLICAR_EVIDENCIA_PRESTACAO; o arquivo vai ao portal
 * quando a PC estiver encerrada e sem ajuste pendente.
 */
const PublicacaoAnexoControl: React.FC<Props> = ({ anexo, onChange }) => {
  const [aberto, setAberto] = useState(false);
  const [descricao, setDescricao] = useState(anexo.descricaoPublica || '');
  const [revisado, setRevisado] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = async (publicavel: boolean) => {
    setErro(null);
    if (publicavel && (!descricao.trim() || !revisado)) {
      setErro('Informe a descrição pública e confirme que o arquivo foi revisado.');
      return;
    }
    setSaving(true);
    try {
      onChange(await prestacaoContaService.definirPublicacaoAnexo(anexo.id, {
        publicavel, descricaoPublica: descricao.trim() || undefined, redacaoConfirmada: publicavel && revisado,
      }));
      setAberto(false);
    } catch (err: unknown) {
      setErro((axios.isAxiosError<{ error?: string }>(err) && err.response?.data?.error) || 'Não foi possível salvar a publicação.');
    } finally {
      setSaving(false);
    }
  };

  if (anexo.publicavel && !aberto) {
    return (
      <span className="inline-flex items-center gap-2 text-xs">
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">No portal</span>
        <button type="button" onClick={() => void salvar(false)} disabled={saving} className="font-semibold text-red-600 hover:underline disabled:opacity-60">Retirar</button>
      </span>
    );
  }
  if (!aberto) {
    return <button type="button" onClick={() => setAberto(true)} className="text-xs font-semibold text-blue-700 hover:underline dark:text-blue-400">Publicar no portal</button>;
  }
  return (
    <div className="mt-2 w-full space-y-2 rounded-xl border border-slate-200 p-3 text-xs dark:border-slate-700">
      <label className="block font-semibold text-slate-700 dark:text-slate-200">
        Descrição pública
        <input
          type="text"
          maxLength={500}
          value={descricao}
          onChange={(e) => setDescricao(toUpperText(e.target.value))}
          placeholder="Ex.: Fotos da turma do curso de fibra de bananeira"
          className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900"
        />
      </label>
      <label className="flex items-start gap-2 text-slate-700 dark:text-slate-200">
        <input type="checkbox" checked={revisado} onChange={(e) => setRevisado(e.target.checked)} className="mt-0.5" />
        Revisei o arquivo: não há CPF, endereço, conta bancária ou outro dado pessoal desnecessário.
      </label>
      {erro && <p role="alert" className="text-red-700 dark:text-red-300">{erro}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => setAberto(false)} disabled={saving} className="rounded-lg border border-slate-300 px-3 py-1 font-semibold dark:border-slate-600">Cancelar</button>
        <button type="button" onClick={() => void salvar(true)} disabled={saving} className="rounded-lg bg-blue-700 px-3 py-1 font-semibold text-white disabled:opacity-60">{saving ? 'Salvando...' : 'Publicar'}</button>
      </div>
    </div>
  );
};

export default PublicacaoAnexoControl;
